// ═══════════════════════════════════════════════════════════════════════════════════════════
// CategoryStyleDialog — a CATEGORY's background colour, icon and short text (2026-10-06,
// _contract/CONTRACT.md §4). Categories only: GroupRow offers the button on a level-1 row and
// nowhere else. Nothing else in the admin changes because of it.
//
// THE THINGS IN THIS FILE THAT ARE CONTRACT, NOT TASTE:
//
//  · SAVE IS A FULL-STATE WRITE. SP20 replaces all three values with what it is sent, so the
//    request ALWAYS carries all three, and "none" travels as null — never as an absent key. A
//    partial body would wipe the two fields the editor did not touch.
//  · IconPath IS UploadFile's `RelativePath`, never its `Url`. The Url is for the preview here and
//    is never sent; the server composes IconUrl itself from PublicBaseUrl.
//  · AN ICON IS NEVER CLEARED BEFORE A 2xx — the same rule ItemDrawer keeps for files. The stored
//    icon stays in state until the upload resolves; a failed upload leaves it exactly as it was.
//  · TYPE / SIZE / HEADER / DIMENSIONS ARE PRE-CHECKED CLIENT-SIDE (png|jpg|jpeg, ≤ 200KB, the
//    real header matching the extension, ≤ 1024×1024) against the same limits ClalCenterLogic
//    carries, purely so the editor gets the icon-specific copy instantly — and before the upload,
//    not as a generic toast at Save. THE SERVER REMAINS THE AUTHORITY — SetCategoryStyle re-reads
//    the real image header and its answer wins.
//  · EVERY upload call is wrapped in try/catch that also catches `TypeError` (platform bug #9,
//    `UploaderAPI.ts:48-55`) — `toErrorKey` is the single funnel, as everywhere in this folder.
//
// RTL (E3 RTL_NOTES 2, as in ConfirmDialog / ClalDrawerStack): MUI v4 portals the Dialog into
// document.body, OUTSIDE App.js's `<div dir>`, so `dir` is set on the Paper explicitly. Every
// direction-dependent style is INLINE at the call site; the makeStyles sheet below is colours,
// sizes and weights only, so jss-rtl flipping it is a no-op. The hex field is LTR content.
//
// THE MINI-PREVIEW is the portal's CLOSED category card (vercel-design-spec "Category card":
// min-height 58, padding 16/20, gap 14, radius 14, icon 26, title 17/600, chevron 20 @ 2.2). Like
// PortalRowPreview it declares direction:'rtl' on its own — the portal is Hebrew, always — so an
// English-UI editor is not shown a mirror image of what the agent gets.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment,
    LinearProgress, TextField, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Check, CloudUploadOutlined, DeleteOutline } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { CC, GroupDto, SetCategoryStyleRequest } from '../../../Models/ClalCenter/ClalCenter';
import { uploadClalFile, toErrorKey } from '../../../redux/reducers/clalCenterSlice';

/** Mirrors ClalCenterLogic MAX_ICON_BYTES / MAX_ICON_PX / MAX_CATEGORY_DESCRIPTION. The server re-checks. */
export const MAX_ICON_BYTES = 200 * 1024;
export const MAX_ICON_PX = 1024;
export const MAX_CATEGORY_DESCRIPTION = 200;
const ICON_EXTENSIONS = ['png', 'jpg', 'jpeg'];

/** The contract's validator, verbatim (§4). The server's is the same pattern. */
const HEX = /^#[0-9A-Fa-f]{6}$/;

/** CONTRACT §6 — admin presets = seed palette = portal defaults. Upper-case, as SP20 stores them. */
export const CATEGORY_PRESETS: Array<{ key: string; hex: string }> = [
    { key: 'presetBlue', hex: '#3D6CED' },
    { key: 'presetPeach', hex: '#FFDDCC' },
    { key: 'presetLightBlue', hex: '#D2E0FF' },
    { key: 'presetLavender', hex: '#EDF3FF' }
];

/** §6: a category with no BgColor is painted this on the portal. */
export const CATEGORY_DEFAULT_BG = '#EDF3FF';
const INK = '#262B3A';      /* --clal-ink  כחול שחור — the dark text colour of §6 */
const STAGE = '#FBF6EF';    /* the link's 430px shell — what the cards actually sit on */
const FONT = '"Assistant", "Segoe UI", Arial, sans-serif';

/**
 * §6, the ONE rule shared with the portal: WCAG relative luminance of the background < 0.4 →
 * white text, else #262B3A. An unparseable value gets the dark ink (it renders on the default wash).
 */
export const categoryTextColor = (bg: string | null | undefined): string => {
    const m = /^#([0-9A-Fa-f]{6})$/.exec((bg ?? '').trim());
    if (!m) return INK;
    const channel = (offset: number) => {
        const c = parseInt(m[1].substr(offset, 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
    return luminance < 0.4 ? '#FFFFFF' : INK;
};

/**
 * The scheme is asserted on this sink for the same reason ItemDrawer asserts it on its href sinks:
 * the value round-trips through the client, and a guard on one sink of two is not a guard.
 */
export const safeIconSrc = (url: string | null | undefined): string | null => {
    const value = (url ?? '').trim();
    return /^https?:\/\//i.test(value) ? value : null;
};

const normColor = (value: string | null | undefined): string | null => {
    const v = (value ?? '').trim();
    return v ? v.toUpperCase() : null;
};

/** The file's bytes. null ⇒ it could not be read, which is treated as a type problem. */
const readFileBytes = (file: File): Promise<Uint8Array | null> =>
    new Promise(resolve => {
        try {
            const reader = new FileReader();
            reader.onload = () => resolve(
                reader.result instanceof ArrayBuffer ? new Uint8Array(reader.result) : null
            );
            reader.onerror = () => resolve(null);
            reader.readAsArrayBuffer(file);
        } catch (e) { resolve(null); }
    });

/**
 * A port of ClalCenterLogic.TryReadImageSize (WebSiteApiNew, ClalCenterLogic.cs:814-866) — same
 * walk, same refusals — because SetCategoryStyle answers ext_not_allowed for a header that does
 * not match the stored extension, while the browser's Image() decode sniffs the content and would
 * accept it (a PNG renamed .jpg, a JPEG whose frame header is not SOF0/1/2).
 *   png        — the 8-byte signature, then IHDR as the FIRST chunk, width/height > 0.
 *   jpg / jpeg — SOI, then the marker walk to the first SOF0/SOF1/SOF2, reached before SOS/EOI with
 *                marker sync intact, width/height > 0.
 * Only the KIND is used here; the dimensions still come from the Image() decode (§4). Keep this in
 * step with the C#: a client stricter than the server blocks valid icons, a looser one is the gap
 * this closes.
 */
const iconHeaderMatches = (data: Uint8Array, ext: string): boolean => {
    if (ext === 'png') {
        const signature = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
        if (data.length < 24) return false;
        for (let i = 0; i < signature.length; i++) {
            if (data[i] !== signature[i]) return false;
        }
        // bytes 8..11 are the IHDR length, 12..15 its type
        if (data[12] !== 0x49 || data[13] !== 0x48 || data[14] !== 0x44 || data[15] !== 0x52) return false;
        // JS `<<` is signed 32-bit like C# int: a set high bit reads negative and fails, as there.
        const width = (data[16] << 24) | (data[17] << 16) | (data[18] << 8) | data[19];
        const height = (data[20] << 24) | (data[21] << 16) | (data[22] << 8) | data[23];
        return width > 0 && height > 0;
    }

    // jpg / jpeg — the only other extension handleIcon lets through.
    if (data.length < 4 || data[0] !== 0xFF || data[1] !== 0xD8) return false;
    let pos = 2;
    while (pos + 1 < data.length) {
        if (data[pos] !== 0xFF) return false;                    // lost sync
        const marker = data[pos + 1];
        if (marker === 0xFF) { pos++; continue; }                // fill byte before a marker
        pos += 2;
        if (marker === 0xD9 || marker === 0xDA) return false;     // EOI / SOS before any frame header
        if ((marker >= 0xD0 && marker <= 0xD7) || marker === 0x01) continue;   // RSTn / TEM: no length
        if (pos + 1 >= data.length) return false;
        const length = (data[pos] << 8) | data[pos + 1];          // includes its own two bytes
        if (length < 2) return false;
        if (marker === 0xC0 || marker === 0xC1 || marker === 0xC2) {
            if (pos + 7 > data.length) return false;              // length(2) precision(1) height(2) width(2)
            const height = (data[pos + 3] << 8) | data[pos + 4];
            const width = (data[pos + 5] << 8) | data[pos + 6];
            return width > 0 && height > 0;
        }
        pos += length;
    }
    return false;
};

/** Reads the decoded size. null ⇒ the browser could not decode it, which is a type problem. */
const readImageSize = (file: File): Promise<{ w: number; h: number } | null> =>
    new Promise(resolve => {
        let url = '';
        try { url = URL.createObjectURL(file); } catch (e) { resolve(null); return; }
        const img = new Image();
        img.onload = () => {
            const size = { w: img.naturalWidth, h: img.naturalHeight };
            URL.revokeObjectURL(url);
            resolve(size);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
        };
        img.src = url;
    });

// Direction-neutral by construction (see the header): colours, sizes, weights — never a side.
// The field rules are ItemDrawer's: MUI's default outline composites to 1.74:1 on white, under
// WCAG 1.4.11's 3:1 floor for a control boundary.
const useStyles = makeStyles({
    fields: {
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--pls-d-text-3, #7C8B9D)' },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--pls-d-text-2, #5A6775)' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--pls-d-clal-blue, #3D6CED)', borderWidth: 2
        },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--pls-d-danger, #B42318)' },
        '& .MuiFormLabel-root': { color: 'var(--pls-d-text-2, #44525E)' },
        '& .MuiFormLabel-root.Mui-focused': { color: 'var(--pls-d-clal-blue, #2F55C4)' },
        '& .MuiFormLabel-root.Mui-error': { color: 'var(--pls-d-danger, #B42318)' },
        '& .MuiFormHelperText-root': { color: 'var(--pls-d-text-2, #5A6775)', fontSize: 12.5 },
        '& .MuiFormHelperText-root.Mui-error': { color: 'var(--pls-d-danger, #B42318)' }
    },
    swatch: {
        '&:focus-visible': { outline: '2px solid var(--pls-d-clal-blue, #3D6CED)', outlineOffset: 2, borderRadius: 8 }
    },
    // Admin chrome is Pulseem, not Clal (ItemDrawer ccSecondary / ccCancel, owner 08.09.2026).
    secondary: {
        '&&': { border: '1px solid var(--pls-d-text-3, #7C8B9D)', color: 'var(--pls-d-text, #424242)', fontWeight: 700, backgroundColor: 'var(--pls-d-surface, #FFFFFF)' },
        '&&:hover': { backgroundColor: 'var(--pls-d-surface-2, #F5F5F5)', borderColor: 'var(--pls-d-brand, #FF1744)' }
    },
    cancel: { '&&': { color: 'var(--pls-d-text-2, #5A6775)', fontWeight: 600 } }
});

const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--pls-d-text-2, #44525E)', marginBottom: 8 };

interface BodyProps {
    group: GroupDto;
    isRTL: boolean;
    busy: boolean;
    onSave: (req: SetCategoryStyleRequest) => void;
    onCancel: () => void;
}

/**
 * The stateful part. Mounted per open (keyed by GroupID, and MUI unmounts a closed Dialog's
 * children), so every open starts from the category's STORED values with no reset effect and no
 * one-frame flash of the previous category's state.
 */
const CategoryStyleBody = ({ group, isRTL, busy, onSave, onCancel }: BodyProps) => {
    const { t } = useTranslation();
    const sheet = useStyles();

    const initial = useMemo(() => ({
        BgColor: normColor(group.BgColor),
        IconPath: (group.IconPath ?? '').trim() || null,
        Description: (group.Description ?? '').trim() || null
    }), [group]);

    const [color, setColor] = useState<string | null>(initial.BgColor);
    const [customText, setCustomText] = useState<string>(initial.BgColor ?? '');
    const [customTouched, setCustomTouched] = useState(false);
    const [iconPath, setIconPath] = useState<string | null>(initial.IconPath);
    const [iconUrl, setIconUrl] = useState<string | null>(initial.IconPath ? group.IconUrl ?? null : null);
    const [description, setDescription] = useState<string>(group.Description ?? '');
    const [progress, setProgress] = useState<number | null>(null);
    /** An i18n key under CC (not a sentence), so a language switch re-renders it correctly. */
    const [iconErrorKey, setIconErrorKey] = useState<string | null>(null);
    const fileInput = useRef<HTMLInputElement | null>(null);
    // An upload can resolve after the dialog closed; it must not write into an unmounted body.
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    const uploading = progress !== null;
    const customValue = customText.trim();
    const customInvalid = customValue !== '' && !HEX.test(customValue);
    // Gated like ItemDrawer's title: not red while the editor is still typing the 7 characters.
    const showCustomError = customInvalid && (customTouched || customValue.length >= 7);

    const request: SetCategoryStyleRequest = {
        GroupID: group.GroupID,
        BgColor: color,
        IconPath: iconPath,
        Description: description.trim() || null
    };
    const dirty =
        request.BgColor !== initial.BgColor ||
        request.IconPath !== initial.IconPath ||
        request.Description !== initial.Description;
    // Invalid hex text means the field and the swatches disagree — nothing is sent until they agree.
    const canSave = dirty && !customInvalid && !uploading && !busy;

    const pickColor = (hex: string | null) => {
        setColor(hex);
        setCustomText(hex ?? '');
        setCustomTouched(false);
    };

    const onCustomChange = (value: string) => {
        setCustomText(value);
        const v = value.trim();
        if (v === '') setColor(null);
        else if (HEX.test(v)) setColor(v.toUpperCase());
        // Anything else: the last valid colour stays selected and Save stays disabled.
    };

    const resetInput = () => { if (fileInput.current) fileInput.current.value = ''; };

    const handleIcon = async (file: File | null) => {
        if (!file) return;
        setIconErrorKey(null);
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (ICON_EXTENSIONS.indexOf(ext) === -1) { setIconErrorKey('categoryStyle.iconTypeError'); resetInput(); return; }
        if (file.size > MAX_ICON_BYTES) { setIconErrorKey('categoryStyle.iconTooLarge'); resetInput(); return; }
        // Bounded read: the size check above caps it at MAX_ICON_BYTES, as on the server.
        const bytes = await readFileBytes(file);
        if (!alive.current) return;
        if (!bytes || !iconHeaderMatches(bytes, ext)) {
            setIconErrorKey('categoryStyle.iconTypeError'); resetInput(); return;
        }
        const size = await readImageSize(file);
        if (!alive.current) return;
        if (!size || !size.w || !size.h) { setIconErrorKey('categoryStyle.iconTypeError'); resetInput(); return; }
        if (size.w > MAX_ICON_PX || size.h > MAX_ICON_PX) {
            setIconErrorKey('categoryStyle.iconTooBigDimensions'); resetInput(); return;
        }

        setProgress(0);
        try {
            const result = await uploadClalFile(file, percent => { if (alive.current) setProgress(percent); });
            if (!alive.current) return;
            // Only NOW is the previous icon replaced — never before a 2xx.
            setIconPath(result.RelativePath);
            setIconUrl(result.Url);
        } catch (error: any) {
            if (!alive.current) return;
            // Catches the interceptor's TypeError as well (platform bug #9). The two keys UploadFile
            // can answer with for an image get the ICON copy — the generic error table says
            // "25MB", which is the item limit, not this one. Anything else: the honest generic.
            const key = toErrorKey(error);
            setIconErrorKey(
                key === 'ext_not_allowed' ? 'categoryStyle.iconTypeError'
                    : key === 'file_too_large' ? 'categoryStyle.iconTooLarge'
                        : 'toast.fileFailed'
            );
        } finally {
            if (alive.current) setProgress(null);
            resetInput();
        }
    };

    const removeIcon = () => {
        setIconPath(null);
        setIconUrl(null);
        setIconErrorKey(null);
    };

    const previewBg = color && HEX.test(color) ? color : CATEGORY_DEFAULT_BG;
    const previewInk = categoryTextColor(previewBg);
    const iconSrc = safeIconSrc(iconUrl);

    // A selected swatch must be perceivable without colour alone: a 2px ink ring AND a check mark,
    // the check painted by the same §6 rule as the card text so it reads on every preset.
    const swatch = (hex: string | null, label: string) => {
        const active = hex === null ? color === null : color === hex;
        return (
            <button
                key={hex ?? 'none'}
                type="button"
                role="radio"
                aria-checked={active}
                title={hex ?? undefined}
                onClick={() => pickColor(hex)}
                className={sheet.swatch}
                style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    width: 72, padding: 2, margin: 0, border: 0, background: 'none',
                    font: 'inherit', cursor: 'pointer'
                }}
            >
                <span
                    aria-hidden="true"
                    style={{
                        boxSizing: 'border-box', width: 36, height: 36, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: active ? `2px solid var(--pls-d-text, ${INK})` : '1px solid var(--pls-d-text-3, #7C8B9D)',
                        // "No colour" is a white disc with a red diagonal — the universal "none".
                        // A gradient angle is direction-neutral, so it needs no isRTL branch.
                        background: hex ?? 'linear-gradient(135deg, transparent 45%, #B42318 45%, #B42318 55%, transparent 55%), #FFFFFF'
                    }}
                >
                    {active && <Check style={{ fontSize: 18, color: hex ? categoryTextColor(hex) : INK }} />}
                </span>
                <span
                    style={{
                        fontSize: 12, lineHeight: 1.25, textAlign: 'center',
                        color: active ? 'var(--pls-d-text, #151b21)' : 'var(--pls-d-text-2, #44525E)', fontWeight: active ? 700 : 500
                    }}
                >
                    {label}
                </span>
            </button>
        );
    };

    return (
        <>
            <DialogTitle id="cc-style-title" disableTypography>
                <Typography component="h2" style={{ fontSize: 18, fontWeight: 800 }}>
                    {t(`${CC}categoryStyle.title`, { name: group.Title })}
                </Typography>
            </DialogTitle>

            <DialogContent className={sheet.fields} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                {/* ── colour ── */}
                <Box>
                    <Typography id="cc-style-color-label" style={LABEL}>
                        {t(`${CC}categoryStyle.color`)}
                    </Typography>
                    <Box
                        role="radiogroup"
                        aria-labelledby="cc-style-color-label"
                        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}
                    >
                        {CATEGORY_PRESETS.map(p => swatch(p.hex, t(`${CC}categoryStyle.${p.key}`)))}
                        {swatch(null, t(`${CC}categoryStyle.colorNone`))}
                    </Box>
                    <TextField
                        id="cc-style-hex"
                        label={t(`${CC}categoryStyle.colorCustom`)}
                        variant="outlined"
                        size="small"
                        value={customText}
                        error={showCustomError}
                        helperText={showCustomError ? t(`${CC}categoryStyle.colorCustomInvalid`) : undefined}
                        onChange={e => onCustomChange(e.target.value)}
                        onBlur={() => setCustomTouched(true)}
                        // A hex code is LTR content inside an RTL paragraph (E3 RTL_NOTES 6). textAlign
                        // is branched, not 'start': the element's own direction:ltr would resolve
                        // 'start' to LEFT even in Hebrew.
                        inputProps={{
                            dir: 'ltr', maxLength: 7, spellCheck: false,
                            style: { direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            boxSizing: 'border-box', display: 'inline-block', width: 18, height: 18,
                                            borderRadius: 4, border: '1px solid #7C8B9D', background: color ?? '#FFFFFF'
                                        }}
                                    />
                                </InputAdornment>
                            )
                        }}
                        style={{ marginTop: 12, width: '100%', maxWidth: 300 }}
                    />
                </Box>

                {/* ── icon ── */}
                <Box>
                    <Typography style={LABEL}>{t(`${CC}categoryStyle.icon`)}</Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        {/* Shown on the colour it will sit on, so a white glyph on "no colour" is
                            caught here and not on the live site. */}
                        <Box
                            aria-hidden="true"
                            style={{
                                boxSizing: 'border-box', width: 56, height: 56, borderRadius: 12, flex: '0 0 auto',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: previewBg,
                                border: iconSrc ? '1px solid var(--pls-d-border, #C9D2DE)' : '2px dashed var(--pls-d-text-3, #7C8B9D)'
                            }}
                        >
                            {iconSrc && <img src={iconSrc} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />}
                        </Box>
                        <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                className={sheet.secondary}
                                startIcon={<CloudUploadOutlined />}
                                disabled={uploading || busy}
                                onClick={() => fileInput.current?.click()}
                            >
                                {iconPath ? t(`${CC}categoryStyle.iconReplace`) : t(`${CC}categoryStyle.iconUpload`)}
                            </Button>
                            {iconPath && (
                                <Button
                                    className={sheet.cancel}
                                    startIcon={<DeleteOutline />}
                                    disabled={uploading || busy}
                                    onClick={removeIcon}
                                >
                                    {t(`${CC}categoryStyle.iconRemove`)}
                                </Button>
                            )}
                        </Box>
                        <input
                            ref={fileInput}
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            style={{ display: 'none' }}
                            onChange={e => handleIcon(e.target.files?.[0] ?? null)}
                        />
                    </Box>
                    {uploading && (
                        <Box role="status" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <LinearProgress
                                variant="determinate"
                                value={progress ?? 0}
                                style={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography component="span" style={{ fontSize: 12.5, color: 'var(--pls-d-text-2, #5A6775)' }}>
                                {t(`${CC}categoryStyle.iconUploading`)}
                            </Typography>
                        </Box>
                    )}
                    {/* Inline, not a toast: it belongs to this field, and it must still be there
                        when the editor looks back at the dialog. */}
                    {iconErrorKey && (
                        <Typography role="alert" style={{ fontSize: 12.5, color: 'var(--pls-d-danger, #B42318)', fontWeight: 600, marginTop: 8 }}>
                            {t(`${CC}${iconErrorKey}`)}
                        </Typography>
                    )}
                    <Typography style={{ fontSize: 12.5, color: 'var(--pls-d-text-2, #5A6775)', marginTop: 8 }}>
                        {t(`${CC}categoryStyle.iconHint`)}
                    </Typography>
                </Box>

                {/* ── text ── */}
                <TextField
                    id="cc-style-desc"
                    label={t(`${CC}categoryStyle.description`)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    // Groups.Description is nvarchar(200). The cap PREVENTS over-length input; a T-SQL
                    // parameter assignment would truncate silently.
                    inputProps={{ maxLength: MAX_CATEGORY_DESCRIPTION }}
                    helperText={
                        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span>{t(`${CC}categoryStyle.descriptionHelp`)}</span>
                            {/* Digits only — no copy to translate. LTR so "37/200" never reorders. */}
                            <span dir="ltr" style={{ flex: '0 0 auto' }}>
                                {`${description.length}/${MAX_CATEGORY_DESCRIPTION}`}
                            </span>
                        </span>
                    }
                />

                {/* ── live mini-preview of the CLOSED card ── */}
                <Box>
                    <Typography style={LABEL}>{t(`${CC}categoryStyle.preview`)}</Typography>
                    <Box style={{ background: STAGE, borderRadius: 12, padding: 14 }}>
                        <Box
                            aria-hidden="true"
                            style={{
                                direction: 'rtl', fontFamily: FONT, boxSizing: 'border-box',
                                width: '100%', maxWidth: 394, minHeight: 58, margin: '0 auto',
                                display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px',
                                borderRadius: 14, background: previewBg, color: previewInk
                            }}
                        >
                            {/* First child under direction:rtl = the RIGHT edge = beside the title's start. */}
                            {iconSrc && (
                                <img
                                    src={iconSrc}
                                    alt=""
                                    style={{ width: 26, height: 26, objectFit: 'contain', flex: '0 0 auto' }}
                                />
                            )}
                            <span style={{ flex: 1, fontSize: 17, fontWeight: 600, letterSpacing: '-.2px', textAlign: 'right' }}>
                                {group.Title}
                            </span>
                            {/* An SVG path never mirrors — the portal's own chevron, not a glyph. */}
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ flex: '0 0 auto' }}
                            >
                                <path d="M6 9l6 6 6-6" />
                            </svg>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions style={{ padding: '12px 20px 18px', gap: 8 }}>
                <Button className={sheet.cancel} onClick={onCancel} disabled={busy}>
                    {t(`${CC}categoryStyle.cancel`)}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    style={{ fontWeight: 700 }}
                    disabled={!canSave}
                    onClick={() => onSave(request)}
                    startIcon={busy ? <CircularProgress size={16} thickness={5} color="inherit" /> : undefined}
                >
                    {t(`${CC}categoryStyle.save`)}
                </Button>
            </DialogActions>
        </>
    );
};

interface Props {
    open: boolean;
    /** The category being styled. null ⇒ closed. */
    group: GroupDto | null;
    isRTL: boolean;
    /** The screen's busy flag — true while SetCategoryStyle is in flight. */
    busy: boolean;
    /** Receives the FULL state. The screen dispatches, refreshes, toasts and closes. */
    onSave: (req: SetCategoryStyleRequest) => void;
    onCancel: () => void;
}

const CategoryStyleDialog = ({ open, group, isRTL, busy, onSave, onCancel }: Props) => {
    // The screen nulls `group` to close. The last one is kept for the exit transition, so the
    // dialog fades out with its content instead of collapsing to an empty paper first.
    const lastGroup = useRef<GroupDto | null>(null);
    if (group) lastGroup.current = group;
    const shown = group ?? lastGroup.current;

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{ dir: isRTL ? 'rtl' : 'ltr' }}
            aria-labelledby="cc-style-title"
        >
            {shown && (
                <CategoryStyleBody
                    key={shown.GroupID}
                    group={shown}
                    isRTL={isRTL}
                    busy={busy}
                    onSave={onSave}
                    onCancel={onCancel}
                />
            )}
        </Dialog>
    );
};

export default CategoryStyleDialog;
